import React, { useEffect, useState } from 'react'
import { CardDetails } from '@features/billing/CreditCardBox/CardDetails'
import { BoxWrapperWithSideImage } from '@features/billing/CreditCardBox/BoxWrapperWithSideImage'
import { StripePaymentProvider } from '@features/billing/stripe/StripePaymentProvider'
import { StripeForm } from '@features/billing/stripe/StripeForm'
import { CreditCardSubmitButtons } from '@features/billing/stripe/CreditCardSubmitButtons'
import { Button } from '@components/Basic/ButtonNbe'
import { useStoreCreditCard } from '@app/api/postCreditCard'
import { sendToast } from '@app/store/actions/ApplicationConfigurationActions'
import { parseApiError } from '@app/api/utils/error'
import { useAppDispatch } from '@app/store/hooks'
import { useGetUserInfo } from '@app/api/getUserInfo'
import { useTranslation } from 'react-i18next'
import { Loader } from '@components/Basic/Loader'
import styled from 'styled-components'
import { BsCreditCard2Back } from 'react-icons/bs'
import { trackEvent } from '@app/dataTracking'
import { makePricingTrackingStep } from '@app/dataTracking/utils'
import { BillingBox } from '@features/billing/BillingBox'
import { BillingFormValues, emptyFormValues } from '@features/billing/BillingBox/formConfig'
import { useGetBillingInfo } from '@app/api/getBillingInfo'
import { useUpdateBillingInfo } from '@app/api/updateBillingInfo'
import { InputFeedback } from '@components/Basic/InputFeedback'

export type CreditCardItem = {
  fullName?: string
  number: string
  expiration: string
  logoUri?: string
}

const ButtonWrapper = styled.div`
  position: relative;

  svg {
    position: absolute;
    z-index: 100;
    top: 50%;
    left: 8%;
    transform: translate(-50%, -50%);
  }
`

export const CreditCardBox: React.FC = () => {
  const [isAddingNewCard, setIsAddingNewCard] = useState(false)
  const [newBillingInfo, setNewBillingInfo] = useState<BillingFormValues>()
  const [isBillingFormValid, setIsBillingFormValid] = useState<boolean>(false)
  const [submittingForms, setSubmittingForms] = useState<boolean>(false)
  const [billingFormErrorMsg, setBillingFormErrorMsg] = useState<string>('')

  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const {
    data: userData,
    isLoading: isLoadingUser,
    refetch: refetchUserInfo,
  } = useGetUserInfo()
  const userBillingInfo = userData?.pricing

  const {
    mutate: updateBillingMutation,
    isLoading: isLoadingBillingMutation,
    error: errorUpdateBilling,
  } = useUpdateBillingInfo()

  const { data: billingData, isLoading: isLoadingBillingInfo, refetch: refetchBillingInfo } =
  useGetBillingInfo()
  const isBillingDataEmpty = billingData && !Object.keys(billingData).length
  const isDataEmpty =
  !(!isBillingDataEmpty && billingData) || billingData.country.length === 0

  const cards: CreditCardItem[] = (
    userBillingInfo && userBillingInfo.creditCard
      ? [
          {
            fullName: userBillingInfo.creditCardOwner || 'Saved card',
            number: userBillingInfo.creditCard,
            expiration: userBillingInfo.creditCardExpiration,
          },
        ]
      : []
  ).filter((card) => card.number && card.number !== 'N/A')

  const {
    mutate: storeCreditCardMutation,
    error: errorStoreCreditCard,
    isLoading: storeLoadingStateCreditCard,
  } = useStoreCreditCard()

  const storeCreditCard = (creditCardToken: string) => {
    storeCreditCardMutation(
      { creditCardToken },
      {
        onSuccess: () => {
          refetchUserInfo()
          refetchBillingInfo()
          setIsAddingNewCard(false)
        },
      }
    )
  }

  const handleStripeSubmit = () => {
    setSubmittingForms(true)
  }

  const handleSubmit = (token: string) => {
    if (newBillingInfo) {
      if (!isBillingFormValid) {
        setBillingFormErrorMsg(t('billing.payment.billingInfoNotValid'))
        return
      }

      updateBillingMutation(
        {
          requestBody: {
            companyName: newBillingInfo.companyName || ' ',
            state: newBillingInfo.state || '',
            country: newBillingInfo.country,
            zip: newBillingInfo.zip,
            phone: newBillingInfo.phone || '',
          },
        },
        {
          onSuccess: () => {
            setBillingFormErrorMsg('')
            storeCreditCard(token)
          },
          onError: (apiError) => {
            const errorMessage = parseApiError(apiError).message
            dispatch(
              sendToast({
                title: 'Warning',
                message: errorMessage,
                color: 'warning',
              })
            )
          },
      })
    } else { 
      // updates only credit card
      setBillingFormErrorMsg('')
      storeCreditCard(token)
    }
  }

  // on error
  useEffect(() => {
    if (errorStoreCreditCard) {
      const parsedError = parseApiError(errorStoreCreditCard)
      dispatch(
        sendToast({
          title: 'Error',
          message: parsedError.message,
          color: 'negative',
        })
      )

      trackEvent({
        eventName: 'UnexpectedErrorThrown',
        feature: 'Pricing',
        step: makePricingTrackingStep(location.pathname),
        params: {
          custom: {
            errorCode: parsedError.code,
            errorDescription: parsedError.message,
          },
        },
        sendEventToIntercom: true,
      })
    }
  }, [errorStoreCreditCard])

  return isLoadingUser || isLoadingBillingInfo ? (
    <div>
      <Loader $active $dimmer $size={'large'} />
    </div>
  ) : isAddingNewCard ? (
    <>
    <BoxWrapperWithSideImage
      hasPadding
      title={t('billing.payment.creditCardBoxTitle')}
    >
      {userData ? (
        <>
          <BillingBox
            onValuesChange={(newValues, isValid) => {
              setBillingFormErrorMsg('')
              setNewBillingInfo(newValues)
              setIsBillingFormValid(isValid)
            }}
            initialFormValues={
              !isBillingDataEmpty && billingData
                ? billingData
                : emptyFormValues
            }
            onStripeSubmit={submittingForms}
            isDataEmpty={isDataEmpty}
            userInfo={userData.user}
          />
          <InputFeedback $status={{ error: billingFormErrorMsg }} />
        </>

      ) : (
        <div>
          <Loader $active $dimmer $size={'large'} />
        </div>
      )}

      <div style={{ margin: '1rem 0 1.5rem' }}>
        <StripePaymentProvider>
          <StripeForm
            onCreditCardTokenRetrieved={(creditCardToken) => handleSubmit(creditCardToken)}
            userBillingInfo={userBillingInfo}
            onStripeSubmit={handleStripeSubmit}
          >
            {({ isLoading }) => (
              <CreditCardSubmitButtons
                isLoading={isLoading || storeLoadingStateCreditCard || isLoadingBillingMutation}
                onCancelRequest={() => {
                  setIsAddingNewCard(false)
                }}
              />
            )}
          </StripeForm>
        </StripePaymentProvider>
      </div>
    </BoxWrapperWithSideImage>

    </>
  ) : (
    <BoxWrapperWithSideImage
      hasPadding
      title={t('billing.payment.creditCardBoxTitle')}
    >
      {cards && cards.length ? (
        cards.map((card, idx) => (
          <CardDetails
            key={idx}
            card={cards[0]}
            onEditCardRequest={() => {
              setIsAddingNewCard(true)
            }}
          />
        ))
      ) : (
        <div>
          <p>{t('billing.payment.noPaymentMethods')}</p>
          <ButtonWrapper>
            <BsCreditCard2Back color={'#fff'} />
            <Button
              onClick={() => {
                setIsAddingNewCard(true)
              }}
              $variant={'action'}
              $size={'large'}
              $fluid
              style={{ maxWidth: '270px' }}
            >
              {t('billing.payment.addCard')}
            </Button>
          </ButtonWrapper>
        </div>
      )}
    </BoxWrapperWithSideImage>
  )
}
